import React, { useEffect, useState } from "react";
import { Clock, DollarSign, FolderCheck, ShieldCheck, TrendingUp, ListChecks } from "lucide-react";
import api from "@/lib/api";

const TASK_LABEL = { filing: "Filings", reminder: "Reminders", request_info: "Info requests", verify_info: "Verifications" };

export default function Insights() {
  const [ins, setIns] = useState(null);
  useEffect(() => { api.get("/insights").then(({ data }) => setIns(data)).catch(() => {}); }, []);
  if (!ins) return <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="h-28 rounded-2xl bg-white/5 animate-pulse" />)}</div>;

  const cards = [
    { icon: Clock, label: "Hours Saved", value: `${ins.hours_saved}h`, tone: "text-dox-magenta" },
    { icon: DollarSign, label: "Money Saved", value: `$${ins.money_saved.toLocaleString()}`, tone: "text-emerald-400" },
    { icon: FolderCheck, label: "Documents Managed", value: ins.documents_managed, tone: "text-white" },
    { icon: ShieldCheck, label: "Deadlines Prevented", value: ins.deadlines_prevented, tone: "text-dox-orange" },
  ];
  const maxTask = Math.max(1, ...ins.most_common_tasks.map((t) => t.count));

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display font-black text-2xl mb-1">Insights</h2>
        <p className="text-white/55 text-sm">The longer you keep Dox, the more valuable it becomes.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} data-testid={`insight-${c.label.toLowerCase().replace(/\s/g, '-')}`} className="glass-card p-5">
            <c.icon size={18} className={c.tone} />
            <div className="mt-3 font-display font-black text-3xl">{c.value}</div>
            <div className="text-xs text-white/45 uppercase tracking-wider mt-0.5">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="glass-card p-6">
          <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><ShieldCheck size={18} className="text-emerald-400" /> Compliance score</h3>
          <div className="flex items-end gap-3 mb-4">
            <span className="font-display font-black text-5xl text-gradient-brand">{ins.compliance_score}%</span>
          </div>
          <div className="flex items-end gap-1 h-24">
            {ins.score_history.length === 0 ? <p className="text-white/40 text-sm">No history yet.</p> :
              ins.score_history.map((p, i) => (
                <div key={i} className="flex-1 bg-gradient-to-t from-dox-orange to-dox-magenta rounded-t" style={{ height: `${Math.max(4, p.score)}%` }} title={`${p.date}: ${p.score}%`} />
              ))}
          </div>
          <div className="text-xs text-white/40 mt-2 flex items-center gap-1"><TrendingUp size={12} /> Business health trend</div>
        </div>

        <div className="glass-card p-6">
          <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><ListChecks size={18} className="text-dox-orange" /> Most common tasks</h3>
          {ins.most_common_tasks.length === 0 ? <p className="text-white/40 text-sm">No tasks yet.</p> : (
            <div className="space-y-3">
              {ins.most_common_tasks.map((t) => (
                <div key={t.type}>
                  <div className="flex justify-between text-sm mb-1"><span className="text-white/70">{TASK_LABEL[t.type] || t.type}</span><span className="text-white/50 font-mono">{t.count}</span></div>
                  <div className="h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-gradient-to-r from-dox-orange to-dox-purple rounded-full" style={{ width: `${(t.count / maxTask) * 100}%` }} /></div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
